import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import styles from './qualityGuardiansForecasting.module.css';

const QualityGuardiansForecasting = () => {
  const [statistics, setStatistics] = useState({
    placed: 740,
    underOffer: 97,
    inMaintenance: 75,
    vacant: 66,
    vetting: 5105,
    potentialSpaces: 5986,
    potentialGuardians: 6083
  });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: ''
  });

  useEffect(() => {
    loadData();
  }, []); // Initial load only - filters applied via handleFilter button

  const loadData = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo
      });
      const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
      const response = await fetch(`${API_BASE_URL}/api/quality/guardians/forecasting?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStatistics(data.data);
      } else {
        console.error('Backend API failed:', response.status, response.statusText);
        toast.error('Failed to load guardians forecasting data');
      }
    } catch (error) {
      console.error('Backend connection error:', error);
      toast.error('Error loading guardians forecasting data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFilter = (e) => {
    e.preventDefault();
    loadData();
  };

  const handleExport = async (format) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
      const response = await fetch(`${API_BASE_URL}/api/quality/guardians/forecasting/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          format,
          ...statistics,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo
        }),
        responseType: 'blob'
      });

      if (response.ok) {
        // Handle blob response for PDF
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `guardians_forecasting_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(`${format.toUpperCase()} export generated successfully`);
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(`Failed to export ${format.toUpperCase()}: ${errorData.message || response.statusText}`);
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Error exporting ${format.toUpperCase()}`);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumb */}
      <div className={styles.row}>
        <div className={styles.colMd12}>
          <ol className={styles.breadcrumb}>
            <li><a href="/dashboard">Dashboard</a></li>
            <li className={styles.active}>Guardians Forecasting</li>
          </ol>
        </div>
      </div>

      {/* Filters Form */}
      <div className={styles.row}>
        <div className={styles.colMd12}>
          <form onSubmit={handleFilter} className={styles.filterForm}>
            <div className={styles.colMd5}>
              <label htmlFor="dateFrom" className={`${styles.controlLabel} ${styles.textRight} ${styles.colSm4}`}>
                Date from:
              </label>
              <div className={styles.colMd8}>
                <input
                  type="date"
                  name="dateFrom"
                  id="dateFrom"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className={`${styles.formControl} ${styles.jsDatepicker}`}
                />
              </div>
            </div>
            <div className={styles.colMd5}>
              <label htmlFor="dateTo" className={`${styles.controlLabel} ${styles.textRight} ${styles.colSm4}`}>
                Date to:
              </label>
              <div className={styles.colMd8}>
                <input
                  type="date"
                  name="dateTo"
                  id="dateTo"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className={`${styles.formControl} ${styles.jsDatepicker}`}
                />
              </div>
            </div>
            <div className={styles.colMd2}>
              <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`}>
                Filter
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Forecasting Table - Centered Layout */}
      <div className={styles.tableContainer}>
        <table className={`${styles.table} ${styles.tableBordered} ${styles.tableCondensed} ${styles.forecastingTable}`}>
          <thead>
            <tr className={styles.tableHeader}>
              <th colSpan="2" className={styles.textCenter}>
                <strong>Guardians Forecasting</strong>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className={styles.tableRow}>
              <td className={styles.metricLabel}>Placed Guardians:</td>
              <td className={`${styles.metricValue} ${styles.textCenter}`}>{statistics.placed}</td>
            </tr>
            <tr className={styles.tableRow}>
              <td className={styles.metricLabel}>Spaces in Under Offer:</td>
              <td className={`${styles.metricValue} ${styles.textCenter}`}>{statistics.underOffer}</td>
            </tr>
            <tr className={styles.tableRow}>
              <td className={styles.metricLabel}>Spaces in Maintenance:</td>
              <td className={`${styles.metricValue} ${styles.textCenter}`}>{statistics.inMaintenance}</td>
            </tr>
            <tr className={styles.tableRow}>
              <td className={styles.metricLabel}>Current Available Spaces:</td>
              <td className={`${styles.metricValue} ${styles.textCenter}`}>{statistics.vacant}</td>
            </tr>
            <tr className={styles.tableRow}>
              <td className={styles.metricLabel}>Spaces in Vetting:</td>
              <td className={`${styles.metricValue} ${styles.textCenter}`}>{statistics.vetting}</td>
            </tr>
            <tr className={`${styles.tableRow} ${styles.highlightedRow}`}>
              <td className={styles.metricLabel}>Potential Spaces:</td>
              <td className={`${styles.metricValue} ${styles.textCenter}`}>{statistics.potentialSpaces}</td>
            </tr>
            <tr className={`${styles.tableRow} ${styles.highlightedRow}`}>
              <td className={styles.metricLabel}>Potential Guardians Forecast:</td>
              <td className={`${styles.metricValue} ${styles.textCenter}`}>{statistics.potentialGuardians}</td>
            </tr>
          </tbody>
        </table>

        {/* Export Controls */}
        <div className={styles.exportControls}>
          <form onSubmit={(e) => { e.preventDefault(); handleExport('pdf'); }}>
            <input type="hidden" name="placed" value={statistics.placed} />
            <input type="hidden" name="underOffer" value={statistics.underOffer} />
            <input type="hidden" name="inMaintenance" value={statistics.inMaintenance} />
            <input type="hidden" name="vacant" value={statistics.vacant} />
            <input type="hidden" name="vetting" value={statistics.vetting} />
            <input type="hidden" name="potentialSpaces" value={statistics.potentialSpaces} />
            <input type="hidden" name="potentialGuardians" value={statistics.potentialGuardians} />
            <input type="hidden" name="dateFrom" value={filters.dateFrom} />
            <input type="hidden" name="dateTo" value={filters.dateTo} />
            
            <button 
              type="submit" 
              name="format" 
              id="exportPdf"
              className={styles.exportButton}
            >
              PDF
            </button>
            <button 
              id="printTable" 
              onClick={handlePrint} 
              type="button"
              className={styles.printButton}
            >
              Print table
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default QualityGuardiansForecasting; 